/**
 * Landlord Onboarding Detail Component
 * Detailed view of a single landlord's onboarding journey with stage management
 */

import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Button,
  Badge,
  Progress,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Icon,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Avatar,
  Checkbox,
  Textarea,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useDisclosure,
  // Tooltip,
  // Tag,
} from '@chakra-ui/react';
import {
  MdOutlineArrowBack,
  MdOutlineCheckCircle,
  MdOutlineWarning,
  MdOutlinePersonSearch,
  MdOutlinePersonAdd,
  MdOutlineEmail,
  MdOutlineLogin,
  MdOutlineAssignment,
  MdOutlineAdd,
  // MdOutlineEdit,
} from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import type {
  LandlordOnboarding,
  // StageDetail,
  // ChecklistItem,
  // OnboardingBlocker,
  BlockerSeverity,
} from '../../../types/landlordOnboarding';
import {
  getStageDisplayName,
  getStageStatusColor,
  getBlockerSeverityColor,
  getStageOrder,
} from '../../../types/landlordOnboarding';

interface LandlordOnboardingDetailProps {
  onboarding: LandlordOnboarding;
  onBack: () => void;
  onUpdate: () => void;
}

const API_BASE = 'https://us-central1-un-dollar-rentflow-dev.cloudfunctions.net';

const LandlordOnboardingDetail: React.FC<LandlordOnboardingDetailProps> = ({
  onboarding,
  onBack,
  onUpdate,
}) => {
  const { t } = useTranslation('landlordOnboarding');
  const toast = useToast();

  const [updating, setUpdating] = useState(false);
  // const [selectedStage, setSelectedStage] = useState<StageDetail | null>(null);
  const [newBlocker, setNewBlocker] = useState({
    stage: onboarding.currentStage,
    severity: 'medium' as BlockerSeverity,
    description: '',
    impact: '',
  });

  const {
    isOpen: isBlockerModalOpen,
    onOpen: onOpenBlockerModal,
    onClose: onCloseBlockerModal,
  } = useDisclosure();

  // Get stage icon
  const getStageIcon = (stage: string) => {
    const icons: Record<string, any> = {
      discovery: MdOutlinePersonSearch,
      provisioning: MdOutlinePersonAdd,
      welcome_sent: MdOutlineEmail,
      first_login: MdOutlineLogin,
      follow_up: MdOutlineCheckCircle,
    };
    return icons[stage] || MdOutlineAssignment;
  };

  // Update checklist item
  const handleToggleChecklistItem = async (
    stageIndex: number,
    itemIndex: number,
    completed: boolean
  ) => {
    setUpdating(true);
    try {
      const response = await fetch(
        `${API_BASE}/updateLandlordOnboardingChecklist`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            onboardingId: onboarding.id,
            stageIndex,
            itemIndex,
            completed,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update checklist');
      }

      toast({
        title: t('detail.checklistUpdated'),
        status: 'success',
        duration: 2000,
      });

      onUpdate();
    } catch (error) {
      console.error('Error updating checklist:', error);
      toast({
        title: t('detail.updateError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Add blocker
  const handleAddBlocker = async () => {
    if (!newBlocker.description || !newBlocker.impact) {
      toast({
        title: t('detail.blockerValidationError'),
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(
        `${API_BASE}/addLandlordOnboardingBlocker`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            onboardingId: onboarding.id,
            ...newBlocker,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add blocker');
      }

      toast({
        title: t('detail.blockerAdded'),
        status: 'success',
        duration: 2000,
      });

      setNewBlocker({
        stage: onboarding.currentStage,
        severity: 'medium',
        description: '',
        impact: '',
      });

      onCloseBlockerModal();
      onUpdate();
    } catch (error) {
      console.error('Error adding blocker:', error);
      toast({
        title: t('detail.addBlockerError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Resolve blocker
  const handleResolveBlocker = async (blockerId: string, resolution: string) => {
    setUpdating(true);
    try {
      const response = await fetch(
        `${API_BASE}/resolveLandlordOnboardingBlocker`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            onboardingId: onboarding.id,
            blockerId,
            resolution,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to resolve blocker');
      }

      toast({
        title: t('detail.blockerResolved'),
        status: 'success',
        duration: 2000,
      });

      onUpdate();
    } catch (error) {
      console.error('Error resolving blocker:', error);
      toast({
        title: t('detail.resolveBlockerError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Format date
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Active blockers
  const activeBlockers = onboarding.blockers.filter((b) => !b.resolvedAt);
  const resolvedBlockers = onboarding.blockers.filter((b) => b.resolvedAt);

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Button
            leftIcon={<Icon as={MdOutlineArrowBack} />}
            onClick={onBack}
            variant="outline"
          >
            {t('common.back')}
          </Button>
          <VStack align="start" spacing={0}>
            <Heading size="md">{onboarding.landlordName}</Heading>
            <Text color="gray.600" fontSize="sm">
              {onboarding.landlordEmail}
            </Text>
          </VStack>
        </HStack>

        <Button
          leftIcon={<Icon as={MdOutlineAdd} />}
          colorScheme="red"
          onClick={onOpenBlockerModal}
        >
          {t('detail.addBlocker')}
        </Button>
      </Flex>

      {/* Quick Stats */}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">
                {t('detail.progress')}
              </StatLabel>
              <StatNumber>{onboarding.progress}%</StatNumber>
              <Progress
                value={onboarding.progress}
                size="sm"
                colorScheme="blue"
                mt={2}
              />
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">
                {t('detail.currentStage')}
              </StatLabel>
              <StatNumber fontSize="lg">
                {getStageDisplayName(onboarding.currentStage)}
              </StatNumber>
              <StatHelpText>
                Stage {getStageOrder(onboarding.currentStage)} of 5
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">
                {t('detail.properties')}
              </StatLabel>
              <StatNumber>{onboarding.propertyCount}</StatNumber>
              <StatHelpText>
                {onboarding.unitCount} units, {onboarding.tenantCount} tenants
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel fontSize="sm">
                {t('detail.activeBlockers')}
              </StatLabel>
              <StatNumber color={activeBlockers.length > 0 ? 'red.500' : 'green.500'}>
                {activeBlockers.length}
              </StatNumber>
              <StatHelpText>
                {resolvedBlockers.length} resolved
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Active Blockers Alert */}
      {activeBlockers.length > 0 && (
        <Card mb={6} bg="red.50" borderColor="red.200" borderWidth={1}>
          <CardHeader>
            <HStack>
              <Icon as={MdOutlineWarning} color="red.500" boxSize={5} />
              <Heading size="sm" color="red.700">
                {t('detail.activeBlockersTitle', {
                  count: activeBlockers.length,
                })}
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <VStack spacing={3} align="stretch">
              {activeBlockers.map((blocker) => (
                <Card key={blocker.id}>
                  <CardBody>
                    <VStack align="stretch" spacing={2}>
                      <Flex justify="space-between" align="start">
                        <VStack align="start" spacing={1} flex={1}>
                          <HStack>
                            <Badge colorScheme={getBlockerSeverityColor(blocker.severity)}>
                              {blocker.severity.toUpperCase()}
                            </Badge>
                            <Badge>{getStageDisplayName(blocker.stage)}</Badge>
                          </HStack>
                          <Text fontWeight="medium">{blocker.description}</Text>
                          <Text fontSize="sm" color="gray.600">
                            Impact: {blocker.impact}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            Created by {blocker.createdByName} on{' '}
                            {formatDate(blocker.createdAt)}
                          </Text>
                        </VStack>
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={() => {
                            const resolution = prompt('Enter resolution details:');
                            if (resolution) {
                              handleResolveBlocker(blocker.id, resolution);
                            }
                          }}
                          isLoading={updating}
                        >
                          {t('detail.resolve')}
                        </Button>
                      </Flex>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Stages */}
      <Card>
        <CardHeader>
          <Heading size="sm">{t('detail.stages')}</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {onboarding.stages.map((stage, stageIndex) => {
              const completedItems = stage.checklist.filter((i) => i.completed).length;
              const totalItems = stage.checklist.length;
              const stageProgress = Math.round((completedItems / totalItems) * 100);

              return (
                <Card
                  key={stage.stage}
                  variant={
                    stage.stage === onboarding.currentStage ? 'outline' : 'elevated'
                  }
                  borderColor={
                    stage.stage === onboarding.currentStage ? 'blue.500' : undefined
                  }
                  borderWidth={stage.stage === onboarding.currentStage ? 2 : undefined}
                >
                  <CardHeader>
                    <Flex justify="space-between" align="center">
                      <HStack>
                        <Icon
                          as={getStageIcon(stage.stage)}
                          boxSize={5}
                          color={`${getStageStatusColor(stage.status)}.500`}
                        />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">
                            {getStageDisplayName(stage.stage)}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {completedItems} of {totalItems} tasks completed
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack>
                        <Badge colorScheme={getStageStatusColor(stage.status)}>
                          {stage.status}
                        </Badge>
                        {stage.assignedToName && (
                          <HStack spacing={2}>
                            <Avatar size="xs" name={stage.assignedToName} />
                            <Text fontSize="sm">{stage.assignedToName}</Text>
                          </HStack>
                        )}
                      </HStack>
                    </Flex>
                    <Progress
                      value={stageProgress}
                      size="sm"
                      colorScheme={getStageStatusColor(stage.status)}
                      mt={3}
                    />
                  </CardHeader>
                  <CardBody pt={0}>
                    <VStack spacing={2} align="stretch">
                      {stage.checklist.map((item, itemIndex) => (
                        <Flex
                          key={item.id}
                          align="center"
                          justify="space-between"
                          p={2}
                          bg={item.completed ? 'green.50' : 'gray.50'}
                          borderRadius="md"
                        >
                          <HStack flex={1}>
                            <Checkbox
                              isChecked={item.completed}
                              onChange={(e) =>
                                handleToggleChecklistItem(
                                  stageIndex,
                                  itemIndex,
                                  e.target.checked
                                )
                              }
                              isDisabled={updating}
                              colorScheme="green"
                            />
                            <Text
                              fontSize="sm"
                              textDecoration={
                                item.completed ? 'line-through' : 'none'
                              }
                              color={item.completed ? 'gray.500' : 'gray.800'}
                            >
                              {item.description}
                            </Text>
                            {item.required && (
                              <Badge colorScheme="orange" fontSize="xs">
                                Required
                              </Badge>
                            )}
                          </HStack>
                          {item.completedAt && (
                            <Text fontSize="xs" color="gray.500">
                              {formatDate(item.completedAt)}
                            </Text>
                          )}
                        </Flex>
                      ))}
                    </VStack>

                    {stage.notes && (
                      <>
                        <Divider my={3} />
                        <Box bg="blue.50" p={3} borderRadius="md">
                          <Text fontSize="sm" fontWeight="medium" mb={1}>
                            Notes:
                          </Text>
                          <Text fontSize="sm" color="gray.700">
                            {stage.notes}
                          </Text>
                        </Box>
                      </>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </VStack>
        </CardBody>
      </Card>

      {/* Add Blocker Modal */}
      <Modal isOpen={isBlockerModalOpen} onClose={onCloseBlockerModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('detail.addBlockerTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Box w="100%">
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {t('detail.blockerStage')}
                </Text>
                <Select
                  value={newBlocker.stage}
                  onChange={(e) =>
                    setNewBlocker({ ...newBlocker, stage: e.target.value as any })
                  }
                >
                  {onboarding.stages.map((stage) => (
                    <option key={stage.stage} value={stage.stage}>
                      {getStageDisplayName(stage.stage)}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box w="100%">
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {t('detail.blockerSeverity')}
                </Text>
                <Select
                  value={newBlocker.severity}
                  onChange={(e) =>
                    setNewBlocker({
                      ...newBlocker,
                      severity: e.target.value as BlockerSeverity,
                    })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </Select>
              </Box>

              <Box w="100%">
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {t('detail.blockerDescription')}
                </Text>
                <Textarea
                  value={newBlocker.description}
                  onChange={(e) =>
                    setNewBlocker({ ...newBlocker, description: e.target.value })
                  }
                  placeholder="What is blocking progress?"
                  rows={3}
                />
              </Box>

              <Box w="100%">
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {t('detail.blockerImpact')}
                </Text>
                <Textarea
                  value={newBlocker.impact}
                  onChange={(e) =>
                    setNewBlocker({ ...newBlocker, impact: e.target.value })
                  }
                  placeholder="What is the impact of this blocker?"
                  rows={3}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseBlockerModal}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="red"
              onClick={handleAddBlocker}
              isLoading={updating}
            >
              {t('detail.addBlocker')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LandlordOnboardingDetail;
